import * as React from 'react';
import useBreakpoints from '@toolkit/util/useBreakpoints';

// Utils
import { formatCents, head, isNilOrEmpty } from '@src/shared/src/util/general';
// Constants
import { ENVIRONMENT } from '@src/shared/src/const/app';
// Actions
// Models
import { TariffModel, OptionModel } from '@src/shared/src/models';
// Interfaces
// Components
import { Spinner } from '@toolkit/ui';
import { CustomSelect } from '@toolkit/ui/CustomSelect';
import { Trans, useTranslation } from 'react-i18next';
// Styles
import '../styles/TariffOptions.scss';

type Props = {
  env: ENVIRONMENT;
  tariffs: TariffModel[];
  selectedTariff: TariffModel;
  changeable?: boolean;
  onChangeTariff: (tariffId: string) => void;
};

const getTariffPriceDifference = (priceDifference: number) =>
  priceDifference < 0 ? formatCents(priceDifference) : `+${formatCents(priceDifference)}`;

const TariffOptions: React.FC<Props> = ({
  env,
  tariffs,
  selectedTariff,
  changeable,
  onChangeTariff,
}) => {
  const { isMd } = useBreakpoints();
  const { t } = useTranslation();

  const toOption = (tariff: TariffModel) => ({
    label: `${getTariffPriceDifference(tariff.priceDifference)} - ${tariff.fareName}`,
    value: String(tariff.id),
    info: tariff.compliant ? null : `${tariff.fareName}\n${t('tariffOptions.option.notCompliant')}`,
  });

  const getOptions = () => {
    const compliants = tariffs.filter((tariff) => tariff.compliant).map(toOption);
    const nonCompliants = tariffs.filter((tariff) => !tariff.compliant).map(toOption);

    return [
      { label: '', options: compliants },
      { label: t('tariffOptions.group.nonCompliant'), options: nonCompliants },
    ];
  };

  const handleOptionChange = (option: OptionModel) =>
    Number(option.value) !== selectedTariff.id ? onChangeTariff(option.value) : null;

  if (isNilOrEmpty(tariffs)) return <Spinner />;

  if (changeable) {
    return (
      <div className="lp-tariff-options-price">
        <strong>{formatCents(head(tariffs).priceDifference)}</strong>
        <div>
          <Trans i18nKey="tariffOptions.label.estimatedPrice">
            <br />
          </Trans>
        </div>
      </div>
    );
  }

  const renderOption = (option) => (
    <span
      className="lp-tariff-option"
      data-rh={isNilOrEmpty(option.info) ? null : option.info}
      data-rh-at="left">
      {option.label}
    </span>
  );

  return (
    <div className="lp-tariff-options">
      <CustomSelect
        menuPlacement={isMd ? 'top' : 'auto'}
        options={getOptions()}
        onChange={handleOptionChange}
        selectedValue={String(selectedTariff.id)}
        isDisabled={env !== ENVIRONMENT.SEARCH}
        renderOption={renderOption}
      />
    </div>
  );
};

export default TariffOptions;
